/**
 * ProgressBar — a determinate progress track.
 *
 * Zero-dep primitive. No platform imports, no internal state; the host owns
 * the number.
 *
 * Deliberately determinate-only: every current consumer (file transfer) knows
 * its own percentage, and an indeterminate barber-pole would misrepresent an
 * encrypted transfer whose real progress we can measure. Add an
 * `indeterminate` mode only when something genuinely cannot know.
 */
import styles from "./ProgressBar.module.css";

export interface ProgressBarProps {
  /**
   * Completion in the range 0–100. Values outside it are clamped rather than
   * rejected: progress is derived arithmetic at every call site (parts done /
   * parts total), and a rounding artefact should not overflow the track or
   * produce an out-of-range `aria-valuenow`.
   */
  value: number;
  /**
   * Accessible name. Required — a bare progressbar role announces only a
   * percentage, which tells a screen-reader user nothing about what is
   * progressing.
   */
  label: string;
  className?: string;
}

export function ProgressBar({ value, label, className }: ProgressBarProps) {
  const pct = Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));
  return (
    <div
      className={className ? `${styles.track} ${className}` : styles.track}
      role="progressbar"
      aria-label={label}
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className={styles.fill} style={{ width: `${pct}%` }} />
    </div>
  );
}
